import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { rhm, apiError } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Btn, Card, Field, Input, PageHead } from "../ui";

export default function RhmSettings() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [draft, setDraft] = useState(null);

    const { data } = useQuery({
        queryKey: ["rhm-settings"],
        queryFn: async () => (await rhm.get("/settings")).data,
    });
    useEffect(() => { if (data) setDraft(data); }, [data]);

    const save = useMutation({
        mutationFn: async (payload) => (await rhm.patch("/settings", payload)).data,
        onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["rhm-settings"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    if (!draft) return <div className="mono text-xs text-zinc-500">LOADING SETTINGS…</div>;
    const editable = can("settings.edit");
    const set = (k, v) => setDraft({ ...draft, [k]: v });

    return (
        <div data-testid="rhm-settings-page">
            <PageHead title="System Settings" sub="Operational thresholds used by alerts and automation."
                action={editable && (
                    <Btn data-testid="rhm-save-settings" disabled={save.isPending}
                        onClick={() => save.mutate({
                            expiry_alert_days: String(draft.expiry_alert_days).split(",").map((s) => Number(String(s).trim())).filter((n) => n > 0),
                            reorder_reminder_before_days: Number(draft.reorder_reminder_before_days),
                            reorder_reminder_after_days: Number(draft.reorder_reminder_after_days),
                            communication_max_per_month: Number(draft.communication_max_per_month),
                            gst_default: Number(draft.gst_default),
                            shipping_free_above: Number(draft.shipping_free_above),
                            shipping_flat: Number(draft.shipping_flat),
                        })}>
                        SAVE SETTINGS
                    </Btn>
                )} />

            <div className="grid md:grid-cols-2 gap-5">
                <Card title="Expiry & Stock Alerts">
                    <Field label="Expiry alert thresholds (days)" hint="Comma separated, e.g. 180,90,60,30">
                        <Input disabled={!editable} value={Array.isArray(draft.expiry_alert_days) ? draft.expiry_alert_days.join(",") : draft.expiry_alert_days}
                            onChange={(e) => set("expiry_alert_days", e.target.value)} data-testid="settings-expiry-days" />
                    </Field>
                    <div className="mt-4 text-sm text-zinc-500">
                        Minimum stock level and reorder quantity are set per product on the product's Commercial tab.
                    </div>
                </Card>

                <Card title="Reorder Reminders">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Remind X days before due">
                            <Input type="number" min="0" disabled={!editable} value={draft.reorder_reminder_before_days}
                                onChange={(e) => set("reorder_reminder_before_days", e.target.value)} data-testid="settings-reorder-before" />
                        </Field>
                        <Field label="Follow up X days after due">
                            <Input type="number" min="0" disabled={!editable} value={draft.reorder_reminder_after_days}
                                onChange={(e) => set("reorder_reminder_after_days", e.target.value)} />
                        </Field>
                        <Field label="Max messages per customer / month" className="col-span-2">
                            <Input type="number" min="1" disabled={!editable} value={draft.communication_max_per_month}
                                onChange={(e) => set("communication_max_per_month", e.target.value)} />
                        </Field>
                    </div>
                </Card>

                <Card title="Commerce Defaults">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Default GST %">
                            <Input type="number" min="0" disabled={!editable} value={draft.gst_default} onChange={(e) => set("gst_default", e.target.value)} />
                        </Field>
                        <Field label="Free shipping above (₹)">
                            <Input type="number" min="0" disabled={!editable} value={draft.shipping_free_above} onChange={(e) => set("shipping_free_above", e.target.value)} />
                        </Field>
                        <Field label="Flat shipping (₹)">
                            <Input type="number" min="0" disabled={!editable} value={draft.shipping_flat} onChange={(e) => set("shipping_flat", e.target.value)} />
                        </Field>
                    </div>
                </Card>

                <Card title="Communication Channels">
                    <div className="text-sm text-zinc-400 space-y-2">
                        <p><span className="text-white font-semibold">Email:</span> engine ready — provider credentials pending (Resend).</p>
                        <p><span className="text-white font-semibold">WhatsApp:</span> official WhatsApp Business Cloud API module pending credentials
                            (Phone Number ID + permanent access token).</p>
                        <p className="text-zinc-500">Until a provider is connected, automated messages are queued and shown as pending.</p>
                    </div>
                </Card>

                <PaymentsCard />

                <AppearanceCard />
            </div>
        </div>
    );
}


// Cashfree status + the webhook address to paste into the Cashfree dashboard.
const PaymentsCard = () => {
    const { data } = useQuery({
        queryKey: ["rhm-payments-config"],
        queryFn: async () => (await rhm.get("/payments/config")).data,
    });
    const webhook = `${window.location.origin}/api/shop/payments/cashfree/webhook`;
    return (
        <Card title="Payments — Cashfree" data-testid="payments-card">
            <div className="text-sm text-zinc-400 space-y-3">
                <p>
                    <span className="text-white font-semibold">Mode:</span>{" "}
                    <span className={data?.mode === "production" ? "text-lime" : "text-orange-300"}
                        data-testid="payments-mode">{(data?.mode || "—").toUpperCase()}</span>
                    {data?.app_id_tail && <span className="text-zinc-500"> · key ···{data.app_id_tail}</span>}
                </p>
                <p>
                    <span className="text-white font-semibold">Status:</span>{" "}
                    {data?.configured ? <span className="text-lime">Connected</span>
                        : <span className="text-red-400">Keys missing</span>}
                </p>
                <div>
                    <div className="mono text-[10px] tracking-[0.16em] text-zinc-500 mb-1">WEBHOOK URL (paste in Cashfree → Developers → Webhooks)</div>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 bg-bg0 border border-white/10 px-3 py-2 text-xs break-all"
                            data-testid="payments-webhook-url">{webhook}</code>
                        <Btn variant="ghost" data-testid="payments-copy-webhook"
                            onClick={() => { navigator.clipboard.writeText(webhook); toast.success("Webhook URL copied"); }}>COPY</Btn>
                    </div>
                </div>
                <p className="text-zinc-500 text-xs">
                    Subscribe to payment success, failure and user-dropped events, webhook version 2025-01-01.
                    Orders only turn Paid after Cashfree confirms the money on our server.
                </p>
            </div>
        </Card>
    );
};

// Super-admin-only switch: does the public website offer the dark theme at all?
const AppearanceCard = () => {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ["site-appearance"],
        queryFn: async () => (await rhm.get("/site/appearance")).data,
    });

    const save = useMutation({
        mutationFn: async (payload) => (await rhm.put("/site/appearance", payload)).data,
        onSuccess: (d) => {
            qc.invalidateQueries({ queryKey: ["site-appearance-public"] });
            toast.success(d.allow_theme_switch
                ? "Visitors can now switch between white and dark"
                : "The website is now white for everyone");
            qc.invalidateQueries({ queryKey: ["site-appearance"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    if (!data) return null;
    const locked = !data.is_super_admin;
    const on = !!data.allow_theme_switch;

    return (
        <Card title="Website Appearance & Shop" data-testid="appearance-card">
            <p className="text-sm text-zinc-400">
                The public website opens in the white (light) theme by default. Turn the switch on only if you want
                visitors to be able to change it themselves.
            </p>

            <div className="mt-4 border border-white/10 p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="font-semibold">Theme option for visitors</div>
                    <div className="text-sm text-zinc-500 mt-1">
                        {on
                            ? "ON — the sun/moon icon appears in the header and visitors can choose white or dark."
                            : "OFF — the whole website is white for everyone and the icon is hidden."}
                    </div>
                </div>
                <button disabled={locked || save.isPending} data-testid="appearance-theme-switch"
                    onClick={() => save.mutate({ allow_theme_switch: !on, default_theme: "light" })}
                    className={`shrink-0 w-16 h-8 rounded-full border transition-colors relative ${
                        on ? "bg-gold/25 border-gold" : "bg-white/5 border-white/15"} ${
                        locked ? "opacity-40 cursor-not-allowed" : ""}`}>
                    <span className={`absolute top-1 w-6 h-6 rounded-full transition-all ${
                        on ? "left-9 bg-gold" : "left-1 bg-zinc-500"}`} />
                </button>
            </div>

            <div className="mt-5 border border-white/10 p-4">
                <div className="font-semibold">Shop sections</div>
                <div className="text-sm text-zinc-500 mt-1">
                    Switch a section off and it disappears from the shop page and the homepage.
                </div>
                <div className="mt-4 space-y-3">
                    {[["products", "Products"], ["consultation_packs", "Consultation Packs"], ["ebooks", "Ebooks & Guides"]]
                        .map(([key, label]) => {
                            const shopOn = (data.shop_sections || {})[key] !== false;
                            return (
                                <div key={key} className="flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="text-sm">{label}</div>
                                        <div className={`text-xs mt-0.5 ${shopOn ? "text-zinc-500" : "text-red-400"}`}
                                            data-testid={`shop-section-state-${key}`}>
                                            {shopOn
                                                ? "ON — visible on the shop page and homepage."
                                                : `OFF — ${label.toLowerCase()} are hidden from the whole website.`}
                                        </div>
                                    </div>
                                    <button disabled={locked || save.isPending} data-testid={`shop-section-${key}`}
                                        onClick={() => save.mutate({
                                            allow_theme_switch: on, default_theme: "light",
                                            shop_sections: { ...(data.shop_sections || {}), [key]: !shopOn },
                                        })}
                                        className={`shrink-0 w-14 h-7 rounded-full border transition-colors relative ${
                                            shopOn ? "bg-gold/25 border-gold" : "bg-white/5 border-white/15"} ${
                                            locked ? "opacity-40 cursor-not-allowed" : ""}`}>
                                        <span className={`absolute top-1 w-5 h-5 rounded-full transition-all ${
                                            shopOn ? "left-8 bg-gold" : "left-1 bg-zinc-500"}`} />
                                    </button>
                                </div>
                            );
                        })}
                </div>
            </div>

            <div className="mt-5 border border-white/10 p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="font-semibold">Demo downloads for paid guides</div>
                    <div className="text-sm text-zinc-500 mt-1">
                        {data.digital_demo_unlock !== false
                            ? "ON — a buyer can download a paid guide as soon as the order is placed. Useful until the payment gateway is live."
                            : "OFF — the download button only unlocks after the order is marked Paid."}
                    </div>
                </div>
                <button disabled={locked || save.isPending} data-testid="digital-demo-unlock-switch"
                    onClick={() => save.mutate({
                        allow_theme_switch: on, default_theme: "light",
                        digital_demo_unlock: data.digital_demo_unlock === false,
                    })}
                    className={`shrink-0 w-16 h-8 rounded-full border transition-colors relative ${
                        data.digital_demo_unlock !== false ? "bg-gold/25 border-gold" : "bg-white/5 border-white/15"} ${
                        locked ? "opacity-40 cursor-not-allowed" : ""}`}>
                    <span className={`absolute top-1 w-6 h-6 rounded-full transition-all ${
                        data.digital_demo_unlock !== false ? "left-9 bg-gold" : "left-1 bg-zinc-500"}`} />
                </button>
            </div>

            <div className="mt-3 mono text-[10px] tracking-[0.16em] text-zinc-500">
                {locked ? "ONLY THE SUPER ADMIN CAN CHANGE THIS" : "SUPER ADMIN CONTROL · APPLIES TO EVERY VISITOR"}
            </div>
            <div className="mt-2 text-xs text-zinc-500">
                The CRM always stays on the dark console theme, whatever this switch says.
            </div>
        </Card>
    );
};
